import React, { useState, useEffect, useMemo } from 'react';
import { SavedTaskBoard, taskboardService, _boardsCache } from '../services/taskboardService';
import { useActiveProject } from '../hooks/useActiveProject';
import '../styles/TaskBoardList.css';
import '../styles/Pages.css';
import { TaskBoardRowsSkeleton } from './SkeletonLoader';
import { ReactComponent as PriorityLowestIcon } from '../icons/priority_lowest.svg';
import { ReactComponent as PriorityLowIcon } from '../icons/priority_low.svg';
import { ReactComponent as PriorityMediumIcon } from '../icons/priority_medium.svg';
import { ReactComponent as PriorityHighIcon } from '../icons/priority_high.svg';
import { ReactComponent as PriorityHighestIcon } from '../icons/priority_highest.svg';

interface TaskBoardListProps {
  onSelectTaskBoard: (taskboard: SavedTaskBoard) => void;
  onNewBoard: () => void;
  onTaskClick?: (taskboard: SavedTaskBoard, taskId: string, columnId: string) => void;
}

// Parse any editor description format to plain text for display
function parseDescriptionText(desc: string): string {
  if (!desc) return '';
  const extractText = (n: any): string => {
    if (typeof n === 'string') return n;
    if (typeof n.text === 'string') return n.text;
    if (Array.isArray(n.children)) return n.children.map(extractText).join(' ');
    return '';
  };
  // Current format: "__editablejs__:[...array...]"
  if (desc.startsWith('__editablejs__:')) {
    try { return (JSON.parse(desc.slice(15)) as any[]).map(extractText).join(' ').replace(/\s+/g, ' ').trim(); } catch { /* fall */ }
  }
  // Legacy JSON object formats: {"__editablejs__":[...]} or {"__editablejs__":true,...}
  if (desc.startsWith('{')) {
    try {
      const p = JSON.parse(desc);
      if (Array.isArray(p.__editablejs__)) return p.__editablejs__.map(extractText).join(' ').replace(/\s+/g, ' ').trim();
      if (p.__editablejs__ && p.type) return [p].map(extractText).join(' ').replace(/\s+/g, ' ').trim();
      if (p.type || p.children || p.text) return [p].map(extractText).join(' ').replace(/\s+/g, ' ').trim();
    } catch { /* fall */ }
  }
  return desc.replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').trim();
}

const TaskBoardList: React.FC<TaskBoardListProps> = ({ onSelectTaskBoard, onNewBoard, onTaskClick }) => {
  // Seed state from in-memory cache for instant render — no spinner if we already have data
  const [taskboards, setTaskboards] = useState<SavedTaskBoard[]>(() => _boardsCache ?? []);
  const [searchQuery, setSearchQuery] = useState('');
  // Only show the full loading spinner when we have nothing to show yet
  const [loading, setLoading] = useState(!_boardsCache);
  const [refreshing, setRefreshing] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const [showDuplicatePopup, setShowDuplicatePopup] = useState(false);
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [selectedBoard, setSelectedBoard] = useState<{ id: string; name: string } | null>(null);
  const [newName, setNewName] = useState('');
  const [showStatusDropdown, setShowStatusDropdown] = useState<string | null>(null);
  const [changingTaskStatus, setChangingTaskStatus] = useState<{ task: any; boardId: string; currentColumnId: string } | null>(null);
  const { activeProjectId } = useActiveProject();

  // Load immediately on mount, debounce subsequent search queries
  useEffect(() => {
    if (!searchQuery) {
      loadTaskBoards();
      return;
    }
    const t = setTimeout(() => loadTaskBoards(), 300);
    return () => clearTimeout(t);
  }, [searchQuery, activeProjectId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleClickOutside = () => {
      if (showStatusDropdown) {
        setShowStatusDropdown(null);
        setChangingTaskStatus(null);
      }
    };

    if (showStatusDropdown) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showStatusDropdown]);

  const loadTaskBoards = async () => {
    try {
      setError(null);
      // If we already have data show it immediately; mark background refresh only
      if (taskboards.length > 0 && !searchQuery) setRefreshing(true);
      const data = await taskboardService.getTaskBoards(searchQuery || undefined, activeProjectId);
      setTaskboards(data);
    } catch (err) {
      setError('Failed to load taskboards');
      console.error(err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleDeleteClick = (id: string, name: string) => {
    setSelectedBoard({ id, name });
    setShowDeletePopup(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedBoard) return;
    try {
      await taskboardService.deleteTaskBoard(selectedBoard.id);
      loadTaskBoards();
      setShowDeletePopup(false);
      setSelectedBoard(null);
    } catch (err) {
      alert('Failed to delete taskboard');
      console.error(err);
    }
  };

  const handleDuplicateClick = (id: string, name: string) => {
    setSelectedBoard({ id, name });
    setNewName(`${name} (Copy)`);
    setShowDuplicatePopup(true);
  };

  const handleDuplicateConfirm = async () => {
    if (!selectedBoard || !newName.trim()) return;
    try {
      await taskboardService.duplicateTaskBoard(selectedBoard.id, newName);
      loadTaskBoards();
      setShowDuplicatePopup(false);
      setSelectedBoard(null);
      setNewName('');
    } catch (err) {
      alert('Failed to duplicate taskboard');
      console.error(err);
    }
  };

  const handleCancelPopup = () => {
    setShowDuplicatePopup(false);
    setShowDeletePopup(false);
    setSelectedBoard(null);
    setNewName('');
  };

  const handleTaskClick = (task: any, boardId: string, columnId: string) => {
    const board = taskboards.find(b => b.id === boardId);
    if (board && onTaskClick) {
      onTaskClick(board, task.id, columnId);
    }
  };

  const handleStatusClick = (e: React.MouseEvent, task: any, boardId: string, columnId: string) => {
    e.stopPropagation();
    setChangingTaskStatus({ task, boardId, currentColumnId: columnId });
    setShowStatusDropdown(task.id);
  };

  const handleStatusChange = async (newColumnId: string, newColumnTitle: string) => {
    if (!changingTaskStatus) return;

    try {
      const board = taskboards.find(b => b.id === changingTaskStatus.boardId);
      if (!board) return;

      // Find the task in the current column
      const currentColumn = board.columns.find(col => col.id === changingTaskStatus.currentColumnId);
      if (!currentColumn) return;

      const taskIndex = currentColumn.tasks.findIndex(t => t.id === changingTaskStatus.task.id);
      if (taskIndex === -1) return;

      // Remove task from current column
      const task = currentColumn.tasks[taskIndex];
      currentColumn.tasks.splice(taskIndex, 1);

      // Add task to new column
      const newColumn = board.columns.find(col => col.id === newColumnId);
      if (newColumn) {
        newColumn.tasks.push(task);
      }

      // Update the board
      await taskboardService.updateTaskBoard(board.id, { columns: board.columns }, board.updated_at);

      // Reload taskboards to reflect the change
      await loadTaskBoards();

      setShowStatusDropdown(null);
      setChangingTaskStatus(null);
    } catch (err) {
      alert('Failed to update task status');
      console.error(err);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  const getTaskCount = (board: SavedTaskBoard) => {
    return board.columns.reduce((total, col) => total + col.tasks.length, 0);
  };

  const allTasksSortedByPriority = useMemo(() => {
    const priorityOrder = { highest: 4, high: 3, medium: 2, low: 1 };
    const allTasks = taskboards.flatMap(board =>
      board.columns.flatMap(column =>
        column.tasks.map(task => ({
          ...task,
          boardId: board.id,
          boardName: board.name,
          columnId: column.id,
          columnTitle: column.title,
          columnColor: column.color
        }))
      )
    );
    return allTasks.sort((a, b) => {
      const priorityA = priorityOrder[a.priority as keyof typeof priorityOrder] || 0;
      const priorityB = priorityOrder[b.priority as keyof typeof priorityOrder] || 0;
      return priorityB - priorityA;
    });
  }, [taskboards]);

  const priorityStats = useMemo(() => {
    const allPriorities = ['highest', 'high', 'medium', 'low', 'lowest'] as const;
    const allTasks = taskboards.flatMap(b => b.columns.flatMap(c => c.tasks));
    const counts: Record<string, number> = {};
    allPriorities.forEach(p => { counts[p] = allTasks.filter(t => t.priority === p).length; });
    return { counts, total: allTasks.length, allPriorities };
  }, [taskboards]);

  const workloadStats = useMemo(() => {
    const workload: Record<string, { total: number; byPriority: Record<string, number> }> = {};
    taskboards.forEach(board =>
      board.columns.forEach(col =>
        col.tasks.forEach(task => {
          const assignee = task.assignee && task.assignee !== 'Unassigned' ? task.assignee : 'Unassigned';
          if (!workload[assignee]) workload[assignee] = { total: 0, byPriority: {} };
          workload[assignee].total += 1;
          const p = task.priority || 'medium';
          workload[assignee].byPriority[p] = (workload[assignee].byPriority[p] || 0) + 1;
        })
      )
    );
    return Object.entries(workload).sort((a, b) => b[1].total - a[1].total).slice(0, 6);
  }, [taskboards]);

  const boardStats = useMemo(() =>
    taskboards.map(board => ({
      name: board.name,
      taskCount: board.columns.reduce((sum, col) => sum + col.tasks.length, 0),
      columnCount: board.columns.length,
    })),
  [taskboards]);

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'highest':
        return '#dc2626'; // Darker red for highest
      case 'high':
        return '#ef4444'; // Red for high
      case 'medium':
        return '#f59e0b'; // Orange for medium
      case 'low':
        return '#3b82f6'; // Blue for low
      default:
        return '#6b7280'; // Gray for none
    }
  };

  const getColumnColor = (columnTitle: string) => {
    const title = columnTitle.toLowerCase();
    if (title.includes('completed') || title.includes('done')) {
      return '#10b981'; // Green for completed
    } else if (title.includes('to do') || title.includes('todo')) {
      return '#f59e0b'; // Yellow/Orange for to do
    } else if (title.includes('in progress') || title.includes('progress')) {
      return '#8b5cf6'; // Purple for in progress
    } else if (title.includes('backlog')) {
      return '#3b82f6'; // Blue for backlog
    }
    return '#6b7280'; // Gray for unknown
  };

  const hexToRgba = (hex: string, alpha: number): string => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'highest':
        return <PriorityHighestIcon width="16" height="16" />;
      case 'high':
        return <PriorityHighIcon width="16" height="16" />;
      case 'medium':
        return <PriorityMediumIcon width="16" height="16" />;
      case 'low':
        return <PriorityLowIcon width="16" height="16" />;
      case 'lowest':
        return <PriorityLowestIcon width="16" height="16" />;
      default:
        return null;
    }
  };

  return (
    <div className="taskboard-list-container">
      {/* Header - Single Row */}
      <div className="taskboard-header-row-1">
        <div className="page-breadcrumb">
          <div className="page-breadcrumb-icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="7" height="7" rx="1" />
              <rect x="14" y="3" width="7" height="7" rx="1" />
              <rect x="3" y="14" width="7" height="7" rx="1" />
              <rect x="14" y="14" width="7" height="7" rx="1" />
            </svg>
          </div>
          <h1 className="page-breadcrumb-title">Task Boards</h1>
        </div>
        <div className="header-right-section">
          <div className="search-bar">
            <svg className="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"></circle>
              <path d="m21 21-4.35-4.35"></path>
            </svg>
            <input
              type="text"
              placeholder="Search task boards..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
          </div>
          <button className="new-taskboard-button" onClick={onNewBoard}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            <span className="new-board-btn-text">New Task Board</span>
          </button>
        </div>
      </div>

      {/* Main Layout with Three Sections */}
      <div className="taskboard-list-main-layout">
        {/* Left Section - 1/3 width - Taskboard List */}
        <div className="taskboard-list-content-section">
          <div className="my-boards-header">
            <h3 className="my-boards-title">My Boards</h3>
          </div>

          <div className="taskboard-list-scrollable">
            {loading && <TaskBoardRowsSkeleton count={5} />}
            {!loading && refreshing && (
              <div style={{ padding: '4px 12px', fontSize: 12, color: '#aaa', textAlign: 'center' }}>Refreshing…</div>
            )}

            {error && (
              <div className="error-state">
                <p>{error}</p>
                <button onClick={loadTaskBoards}>Retry</button>
              </div>
            )}

            {!loading && !error && taskboards.length === 0 && (
              <div className="empty-state">
                <div className="empty-icon">📋</div>
                <h2>{searchQuery ? 'No task boards found' : 'No task boards yet'}</h2>
                <p>{searchQuery ? 'Try a different search term' : 'Create your first task board to organize your work'}</p>
              </div>
            )}

            {!loading && !error && taskboards.length > 0 && (
              <div className="boards-simple-list">
                {taskboards.map((board, idx) => {
                  const taskCount = getTaskCount(board);
                  const colCount = board.columns.length;
                  // Cycle through 4 accent colours matching the image
                  const iconColors = ['#6366f1', '#ec4899', '#f59e0b', '#10b981'];
                  const iconBgs   = ['#ede9fe', '#fce7f3', '#fef3c7', '#d1fae5'];
                  const colorIdx  = idx % 4;
                  return (
                    <div
                      key={board.id}
                      className="board-list-row"
                      onClick={() => onSelectTaskBoard(board)}
                    >
                      {/* Coloured icon box */}
                      <div className="board-list-icon" style={{ background: iconBgs[colorIdx] }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={iconColors[colorIdx]} strokeWidth="2">
                          <rect x="3" y="3" width="7" height="7" rx="1"/>
                          <rect x="14" y="3" width="7" height="7" rx="1"/>
                          <rect x="14" y="14" width="7" height="7" rx="1"/>
                          <rect x="3" y="14" width="7" height="7" rx="1"/>
                        </svg>
                      </div>

                      {/* Name + meta */}
                      <div className="board-list-info">
                        <span className="board-list-name">{board.name}</span>
                        <span className="board-list-meta">{taskCount} {taskCount === 1 ? 'task' : 'tasks'} &nbsp;•&nbsp; {colCount} {colCount === 1 ? 'column' : 'columns'}</span>
                      </div>

                      {/* Actions (visible on hover) + chevron */}
                      <div className="board-list-right">
                        <div className="board-list-actions">
                          <button
                            className="board-list-action-btn"
                            onClick={(e) => { e.stopPropagation(); handleDuplicateClick(board.id, board.name); }}
                            title="Duplicate"
                          >
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                          </button>
                          <button
                            className="board-list-action-btn delete"
                            onClick={(e) => { e.stopPropagation(); handleDeleteClick(board.id, board.name); }}
                            title="Delete"
                          >
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                          </button>
                        </div>
                        <svg className="board-list-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2.5"><polyline points="9 18 15 12 9 6"/></svg>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>


        {/* Middle Section - 1/3 width - All Tasks by Priority */}
        <div className="taskboard-list-middle-section">
          <h3 className="sidebar-section-title">All Tasks by Priority</h3>
          <div className="sidebar-tasks-list">
            {allTasksSortedByPriority.length === 0 ? (
              <p className="sidebar-section-placeholder">No tasks available</p>
            ) : (
              allTasksSortedByPriority.map((task, index) => {
                const descText = parseDescriptionText(task.description);
                return (
                <div
                  key={`${task.id}-${index}`}
                  className="sidebar-task-item"
                  onClick={() => handleTaskClick(task, task.boardId, task.columnId)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="sidebar-task-header">
                    <div className="sidebar-task-priority-icon">
                      {getPriorityIcon(task.priority || '')}
                    </div>
                    <span className="sidebar-task-board">{task.boardName}</span>
                  </div>
                  <h4 className="sidebar-task-title">{task.title}</h4>
                  {descText && (
                    <p className="sidebar-task-description">
                      {descText.slice(0, 120)}
                      {descText.length > 120 ? '…' : ''}
                    </p>
                  )}
                  <div className="sidebar-task-footer">
                    <div className="sidebar-task-column-wrapper" style={{ position: 'relative' }}>
                      <span
                        className="sidebar-task-column"
                        style={{ color: getColumnColor(task.columnTitle), cursor: 'pointer' }}
                        onClick={(e) => handleStatusClick(e, task, task.boardId, task.columnId)}
                      >
                        {task.columnTitle}
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginLeft: '4px', verticalAlign: 'middle' }}>
                          <polyline points="6 9 12 15 18 9"></polyline>
                        </svg>
                      </span>
                      {showStatusDropdown === task.id && (
                        <div className="status-dropdown">
                          {taskboards.find(b => b.id === task.boardId)?.columns.map(col => (
                            <div
                              key={col.id}
                              className="status-dropdown-item"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleStatusChange(col.id, col.title);
                              }}
                              style={{
                                color: getColumnColor(col.title),
                                fontWeight: col.id === task.columnId ? 'bold' : 'normal'
                              }}
                            >
                              {col.title}
                              {col.id === task.columnId && ' ✓'}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    {task.tags && task.tags.length > 0 && (
                      <div className="sidebar-task-tags">
                        {task.tags.slice(0, 2).map((tag: string, idx: number) => (
                          <span key={idx} className="sidebar-task-tag">{tag}</span>
                        ))}
                        {task.tags.length > 2 && (
                          <span className="sidebar-task-tag">+{task.tags.length - 2}</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );})
            )}
          </div>
        </div>

        {/* Right Section - 1/3 width - Statistics */}
        <div className="taskboard-list-right-section">
          {/* Upper Partition - Task Statistics */}
          <div className="stats-partition">
            <h3 className="sidebar-section-title">Task Statistics</h3>
            <div className="stats-chart-container">
              <div className="stats-content">
                <div className="stats-summary">
                  <div className="stats-summary-item">
                    <span className="stats-number">{priorityStats.total}</span>
                    <span className="stats-label">Total Tasks</span>
                  </div>
                </div>
                <div className="stats-bars">
                  {priorityStats.allPriorities.map((priority) => {
                    const count = priorityStats.counts[priority];
                    const percentage = priorityStats.total > 0 ? (count / priorityStats.total) * 100 : 0;
                    const barWidth = count > 0 ? Math.max(percentage, 3) : 0;
                    return (
                      <div key={priority} className="stats-bar-row">
                        <div className="stats-bar-label">
                          <span className={`priority-indicator priority-${priority}`}></span>
                          <span className="stats-bar-text">{priority}</span>
                        </div>
                        <div className="stats-bar-wrapper">
                          <div className={`stats-bar-fill priority-${priority}-bg`} style={{ width: `${barWidth}%` }}></div>
                        </div>
                        <span className="stats-bar-count">{count}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* User Workload Partition */}
          <div className="stats-partition">
            <h3 className="sidebar-section-title">User Workload</h3>
            <div className="stats-chart-container">
              {workloadStats.length === 0 ? (
                <p className="sidebar-section-placeholder">No assignees yet</p>
              ) : (() => {
                const maxCount = Math.max(...workloadStats.map(([, v]) => v.total), 1);
                const avatarColors = ['#6366f1','#ec4899','#f59e0b','#10b981','#3b82f6','#8b5cf6'];
                const totalAssigned = workloadStats.reduce((s, [, v]) => s + v.total, 0);
                return (
                  <div className="stats-content">
                    <div className="stats-summary">
                      <div className="stats-summary-item">
                        <span className="stats-number" style={{ color: '#6366f1' }}>{workloadStats.filter(([name]) => name !== 'Unassigned').length}</span>
                        <span className="stats-label">Active Members</span>
                      </div>
                    </div>
                    <div className="stats-bars">
                      {workloadStats.map(([name, data], idx) => {
                        const pct = Math.round((data.total / maxCount) * 100);
                        const isUnassigned = name === 'Unassigned';
                        const color = isUnassigned ? '#9ca3af' : avatarColors[idx % avatarColors.length];
                        return (
                          <div key={name} className="workload-bar-row">
                            <div className="workload-avatar" style={{ background: isUnassigned ? '#f3f4f6' : color + '22', color: isUnassigned ? '#9ca3af' : color, border: `1.5px solid ${isUnassigned ? '#e5e7eb' : color + '55'}` }}>
                              {isUnassigned ? (
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>
                              ) : name.charAt(0).toUpperCase()}
                            </div>
                            <div className="workload-bar-body">
                              <div className="workload-bar-label-row">
                                <span className="workload-name" style={{ color: isUnassigned ? '#9ca3af' : '#374151' }}>{name}</span>
                                <span className="workload-count" style={{ color }}>{data.total} task{data.total !== 1 ? 's' : ''}</span>
                              </div>
                              <div className="workload-bar-track">
                                <div className="workload-bar-fill" style={{ width: `${pct}%`, background: isUnassigned ? '#e5e7eb' : `linear-gradient(90deg, ${color}cc, ${color})` }} />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="workload-footer">
                      <span className="workload-footer-text">{totalAssigned} tasks assigned in total</span>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Lower Partition - Board Statistics */}
          <div className="stats-partition">
            <h3 className="sidebar-section-title">Board Statistics</h3>
            <div className="stats-chart-container">
              {(() => {
                const maxTasks = Math.max(...boardStats.map(b => b.taskCount), 1);
                return (
                  <div className="stats-content">
                    <div className="stats-summary">
                      <div className="stats-summary-item">
                        <span className="stats-number">{taskboards.length}</span>
                        <span className="stats-label">Active Boards</span>
                      </div>
                    </div>
                    <div className="stats-bars">
                      {boardStats.slice(0, 5).map((board, index) => (
                        <div key={index} className="stats-bar-row">
                          <div className="stats-bar-label">
                            <span className="stats-bar-text board-name">{board.name}</span>
                          </div>
                          <div className="stats-bar-wrapper">
                            <div className="stats-bar-fill board-bar-bg" style={{ width: `${(board.taskCount / maxTasks) * 100}%` }}></div>
                          </div>
                          <span className="stats-bar-count">{board.taskCount}</span>
                        </div>
                      ))}
                      {boardStats.length > 5 && (
                        <p className="stats-more">+{boardStats.length - 5} more boards</p>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      </div>

      {/* Duplicate Popup */}
      {showDuplicatePopup && (
        <div className="modal-overlay" onClick={handleCancelPopup}>
          <div className="modal-popup" onClick={(e) => e.stopPropagation()}>
            <h2>Duplicate Task Board</h2>
            <p>Enter a name for the duplicated task board:</p>
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Task board name"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleDuplicateConfirm();
                if (e.key === 'Escape') handleCancelPopup();
              }}
            />
            <div className="modal-actions">
              <button className="modal-btn-cancel" onClick={handleCancelPopup}>
                Cancel
              </button>
              <button
                className="modal-btn-primary"
                onClick={handleDuplicateConfirm}
                disabled={!newName.trim()}
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Popup */}
      {showDeletePopup && selectedBoard && (
        <div className="modal-overlay" onClick={handleCancelPopup}>
          <div className="modal-popup" onClick={(e) => e.stopPropagation()}>
            <h2>Delete Task Board</h2>
            <p>Are you sure you want to delete <strong>"{selectedBoard.name}"</strong>?</p>
            <p className="modal-warning">This action cannot be undone.</p>
            <div className="modal-actions">
              <button className="modal-btn-cancel" onClick={handleCancelPopup}>
                Cancel
              </button>
              <button className="modal-btn-danger" onClick={handleDeleteConfirm}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskBoardList;
